import { Link } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';

interface PageHeaderProps {
  /**
   * Visual element shown next to the title. Can be an emoji (🏆 ⚽ 🏀),
   * a Lucide icon, or a flag. Optional — pass nothing for a text-only
   * header.
   */
  icon?: ReactNode;
  title: string;
  /**
   * Secondary line under the title. Use this for channel counts,
   * short descriptions, or status info.
   */
  subtitle?: ReactNode;
  /**
   * If set, renders a small `← Back` link above the title pointing
   * to this path. Defaults to `/` (Home).
   */
  backTo?: string;
  backLabel?: string;
  /**
   * Right-side slot for action buttons (filters, "View all" links,
   * refresh buttons, etc.). Sized to align with the title row.
   */
  action?: ReactNode;
  /**
   * `size` controls the title font weight and emoji sizing so that
   * the top-level page header feels slightly larger than a section
   * header. Defaults to `page`.
   */
  size?: 'page' | 'section';
}

/**
 * Standard header used by the content pages (Home, World Cup, Top
 * Sports, sport category, country, all channels, etc.). The visual
 * treatment is consistent so navigating between pages feels stable:
 *   ┌────────────────────────────────────────────────────┐
 *   │ ← Back                                              │
 *   │  🏆  World Cup Broadcasters        [View all →]    │
 *   │  12 curated channels                                │
 *   └────────────────────────────────────────────────────┘
 *
 * The `backTo` link is intentionally small and unobtrusive so it
 * doesn't fight the title for attention.
 */
export function PageHeader({
  icon,
  title,
  subtitle,
  backTo,
  backLabel = 'Back',
  action,
  size = 'page',
}: PageHeaderProps) {
  const iconClass = size === 'page' ? 'text-3xl' : 'text-2xl';
  const titleClass =
    size === 'page' ? 'text-2xl font-bold' : 'text-lg font-semibold';

  return (
    <div>
      {backTo && (
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text mb-2 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {backLabel}
        </Link>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          {icon && (
            <span className={`${iconClass} flex-shrink-0`} aria-hidden="true">
              {icon}
            </span>
          )}
          <div className="min-w-0">
            <h1 className={titleClass}>{title}</h1>
            {subtitle && (
              <div className="text-text-muted text-sm mt-0.5">{subtitle}</div>
            )}
          </div>
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    </div>
  );
}
